import { QuestionStatus } from '@exam-management/graphql';
import {
  FormSelection,
  EllipsisText,
  Text,
  CircleIcon,
  type GridColDef,
  ColorPalette,
  Flex,
} from '@shared/components';

import type { QuestionReviewUpdateQuestionStatusRow } from '../../types';

export interface StatusOption {
  label: string;
  value: QuestionStatus;
}

export function getStatusLabel(status: QuestionStatus) {
  switch (status) {
    case QuestionStatus.Draft:
      return 'Draft';
    case QuestionStatus.PreDraft:
      return 'Pre-Draft';
    case QuestionStatus.Disabled:
      return 'Disabled';
    default:
      return 'Unknown';
  }
}

export function getLabelColor(status: QuestionStatus) {
  switch (status) {
    case QuestionStatus.Draft:
      return ColorPalette.INFO;
    case QuestionStatus.PreDraft:
      return ColorPalette.INFO;
    case QuestionStatus.Disabled:
      return ColorPalette.SECONDARY;
    default:
      return ColorPalette.PRIMARY;
  }
}

export const statusOptions: StatusOption[] = [
  QuestionStatus.PreDraft,
  QuestionStatus.Draft,
  QuestionStatus.Disabled,
].map((status) => ({
  label: getStatusLabel(status),
  value: status,
}));

export function StatusItem({ status }: Readonly<{ status: QuestionStatus }>) {
  return (
    <Flex sx={{ alignItems: 'center' }}>
      <CircleIcon
        sx={(theme) => ({
          width: '10px',
          height: '10px',
          mr: theme.spacing(2),
          color:
            status === QuestionStatus.PreDraft
              ? theme.palette.info.light
              : theme.palette[getLabelColor(status)].main,
        })}
      />
      <Text>{getStatusLabel(status)}</Text>
    </Flex>
  );
}

const columns: GridColDef<QuestionReviewUpdateQuestionStatusRow>[] = [
  {
    field: 'order',
    headerName: 'No',
    width: 36,
    sortable: false,
    renderCell: (params) => <Text>{params.row.order}</Text>,
  },
  {
    field: 'QID',
    headerName: 'QID',
    flex: 1,
    sortable: false,
    renderCell: (params) => <EllipsisText>{params.row.qid}</EllipsisText>,
  },
  {
    field: 'toStatus',
    headerName: 'Question Status',
    width: 160,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      return (
        <FormSelection
          name={`questions.${params.id}`}
          placeholder='Select Status'
          options={statusOptions}
          sx={{ maxWidth: 160, width: '100%', '& .MuiInputBase-root': { height: 32 } }}
          size='small'
          itemComponent={({ option }) => {
            const status = option.value as QuestionStatus;

            return <StatusItem status={status} />;
          }}
          slotProps={{
            select: {
              renderValue: (value) => {
                const status = value as QuestionStatus;
                return <StatusItem status={status} />;
              },
            },
          }}
        />
      );
    },
  },
];

export default columns;
